// src/tabs/logements/TaxeBaremeDetector.jsx — LOT TAXE 0.4 (commit D)
//
// Détection du barème de taxe de séjour à la saisie d'adresse (arbitrages
// Dimitri 2026-07-12) — rendu dans la carte « Adresse » (Livret >
// Instructions). Au save/blur d'`adresse_complete`, parse CP+ville
// (parseAddressCityCp, miroir lib/migrate-kv-livret-helpers::parseAddress +
// normalisation Q5) puis interroge `taxe_bareme_detect` (requireAdmin) :
//
//   - match 'valide'  → bandeau « Barème détecté : X — Appliquer » : 1 clic →
//     onApplyPatchAndSave({ taxe_sejour: {...} }) AWAITÉ, erreur remontée.
//   - match 'propose' / 'externe' → « Proposition trouvée (source) — Valider
//     ce barème » : 3 champs préremplis + AVERTISSEMENT Q7 (taux BRUT,
//     additionnelles départementale +10 % / régionale +34 % en Haute-Garonne)
//     + lien source cliquable. « Valider et appliquer » → écrit le catalogue
//     (taxe_bareme_validate, clic Dimitri) PUIS applique au logement.
//   - match 'none' → « Aucun barème fiable — saisir manuellement » (3 champs
//     + même avertissement).
//
// Q1 STRICT : tant que le logement n'a pas de barème appliqué, les devis
// publics répondent `bareme_non_valide` (bloc résa « bientôt disponible »
// côté fbz-sites) — le bandeau le rappelle.
//
// Chambres lc1-lc3 : PAS de « Copier vers chambres » ici (les champs
// tarifaires sont exclus de la whitelist copy-to-chambres — décision à part
// si besoin) ; les 3 chambres partagent l'adresse → même détection, 1 clic
// chacune.

import { useState, useEffect, useRef, useCallback } from "react";
import { DS } from "../../lib/ui";
import { FicheAlert, PrimaryButton, SecondaryButton } from "./_ficheUi";
import {
  parseAddressCityCp,
  hasValidBareme,
  resumeBareme,
} from "../../lib/taxe-commune-helpers";
import { detectTaxeBareme, validateTaxeBareme, emailSessionCourante } from "../../lib/taxe-api";

const num2 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export default function TaxeBaremeDetector({ log, cfg, onApplyPatchAndSave }) {
  const parsed = parseAddressCityCp(log?.adresse_complete);
  const baremeOk = hasValidBareme(log, cfg);
  const resume = resumeBareme(log, cfg);

  const [status, setStatus] = useState("idle"); // idle|loading|done|error
  const [result, setResult] = useState(null); // réponse detect
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applied, setApplied] = useState(false);
  // Saisie manuelle / validation proposition — 3 champs.
  const [manual, setManual] = useState(null); // { taux_pct, plafond_eur, mineurs_exoneres, source_url? , id? }
  const debounceRef = useRef(null);
  const lastKeyRef = useRef(null);

  const runDetect = useCallback(async (city, cp) => {
    setStatus("loading");
    setResult(null);
    setManual(null);
    setApplyError(null);
    try {
      const out = await detectTaxeBareme({ commune: city, code_postal: cp });
      setResult(out);
      setStatus("done");
    } catch (e) {
      console.error("[TaxeBaremeDetector] detect", e);
      setStatus("error");
    }
  }, []);

  // Détection auto (debounce 700 ms) quand l'adresse parse ET qu'aucun barème
  // valide n'est posé — jamais de re-détection intempestive sur un logement
  // déjà validé (bouton manuel disponible en dessous).
  useEffect(() => {
    if (!parsed || baremeOk || applied) return undefined;
    const key = `${parsed.city}|${parsed.postal_code}`;
    if (lastKeyRef.current === key) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      lastKeyRef.current = key;
      runDetect(parsed.city, parsed.postal_code);
    }, 700);
    return () => clearTimeout(debounceRef.current);
  }, [parsed?.city, parsed?.postal_code, baremeOk, applied, runDetect]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Application au logement : 1 clic, save AWAITÉ, erreur remontée ────────
  // Métadonnées d'origine (29/09) : les MÊMES que la saisie groupée
  // (TaxeParcPanel) — source, saisi_le, saisi_par — pour que l'écran « tout le
  // parc » ne dise plus « posé avant cet écran, non vérifié » d'un barème posé
  // ici. Les valeurs (taux, plafond, mineurs) ne changent pas ; le prix ne lit
  // qu'elles. Un email introuvable n'empêche jamais d'appliquer (saisi_par null).
  async function applyBareme({ taux_pct, plafond_eur, mineurs_exoneres }, source) {
    setApplying(true);
    setApplyError(null);
    try {
      let saisiPar = null;
      try {
        saisiPar = await emailSessionCourante();
      } catch (e) {
        console.error("[TaxeBaremeDetector] session", e);
      }
      const r = await onApplyPatchAndSave({
        taxe_sejour: {
          taux_pct,
          plafond_eur,
          mineurs_exoneres: mineurs_exoneres !== false,
          source: source || null,
          saisi_le: new Date().toISOString(),
          saisi_par: saisiPar,
        },
      });
      if (!r || r.ok !== true) {
        setApplyError((r && r.error) || "Enregistrement échoué");
        return false;
      }
      setApplied(true);
      return true;
    } catch (e) {
      console.error("[TaxeBaremeDetector] apply", e);
      setApplyError(e.message || "Enregistrement échoué");
      return false;
    } finally {
      setApplying(false);
    }
  }

  // ── Validation catalogue (proposition/saisie manuelle) PUIS application ───
  async function validateThenApply() {
    const taux = num2(manual?.taux_pct);
    const plafond = num2(manual?.plafond_eur);
    if (taux == null || taux <= 0 || plafond == null || plafond <= 0) {
      setApplyError("Taux et plafond doivent être des nombres > 0.");
      return;
    }
    setApplying(true);
    setApplyError(null);
    try {
      const body = manual.id
        ? { id: manual.id, taux_pct: taux, plafond_eur: plafond, mineurs_exoneres: manual.mineurs_exoneres !== false, source_url: manual.source_url || undefined }
        : {
            commune_nom: parsed?.city || "",
            code_postal: parsed?.postal_code || "",
            epci: manual.epci || undefined,
            taux_pct: taux,
            plafond_eur: plafond,
            mineurs_exoneres: manual.mineurs_exoneres !== false,
            source_url: manual.source_url || undefined,
          };
      await validateTaxeBareme(body); // écrit le catalogue (clic admin — Q7)
      await applyBareme(
        { taux_pct: taux, plafond_eur: plafond, mineurs_exoneres: manual.mineurs_exoneres !== false },
        manual.source_url || "saisie depuis la fiche logement (catalogue validé)",
      );
    } catch (e) {
      console.error("[TaxeBaremeDetector] validate", e);
      setApplyError(e.message || "Validation échouée");
      setApplying(false);
    }
  }

  const manualForm = (title) => (
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: DS.marine }}>{title}</div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ fontSize: 12, color: DS.marine }}>
          Taux (%){" "}
          <input
            type="number" step="0.01" min="0"
            value={manual?.taux_pct ?? ""}
            onChange={(e) => setManual((m) => ({ ...m, taux_pct: e.target.value }))}
            style={{ width: 80, padding: "6px 8px", border: `1px solid ${DS.border}`, borderRadius: 6, fontSize: 12 }}
            data-testid="taxe-manual-taux"
          />
        </label>
        <label style={{ fontSize: 12, color: DS.marine }}>
          Plafond (€/nuit/pers){" "}
          <input
            type="number" step="0.01" min="0"
            value={manual?.plafond_eur ?? ""}
            onChange={(e) => setManual((m) => ({ ...m, plafond_eur: e.target.value }))}
            style={{ width: 80, padding: "6px 8px", border: `1px solid ${DS.border}`, borderRadius: 6, fontSize: 12 }}
            data-testid="taxe-manual-plafond"
          />
        </label>
        <label style={{ fontSize: 12, color: DS.marine, display: "flex", alignItems: "center", gap: 5 }}>
          <input
            type="checkbox"
            checked={manual?.mineurs_exoneres !== false}
            onChange={(e) => setManual((m) => ({ ...m, mineurs_exoneres: e.target.checked }))}
          />
          Mineurs exonérés
        </label>
      </div>
      {/* Avertissement Q7 OBLIGATOIRE dès qu'un taux vient d'une source externe
          ou d'une saisie : le taux communal BRUT n'inclut pas forcément les
          taxes additionnelles. */}
      <div style={{ fontSize: 11, color: DS.warningText, background: DS.warningBg, padding: "6px 8px", borderRadius: 6 }}>
        ⚠️ Vérifiez les <strong>taxes additionnelles</strong> (départementale +10 %,
        régionale +34 % en Haute-Garonne) — le taux légal peut être supérieur au
        taux communal brut.
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <PrimaryButton onClick={validateThenApply} disabled={applying} style={{ fontSize: 12 }}>
          {applying ? "Validation…" : "Valider ce barème et l'appliquer"}
        </PrimaryButton>
        <SecondaryButton onClick={() => { setManual(null); setApplyError(null); }} disabled={applying} style={{ fontSize: 12 }}>
          Annuler
        </SecondaryButton>
      </div>
    </div>
  );

  // ── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ marginTop: 10 }} data-testid="taxe-bareme-detector">
      {/* État courant du barème du logement. */}
      {baremeOk ? (
        <FicheAlert tone="success" testId="taxe-bareme-ok">
          Taxe de séjour : barème {resume.source === "logement" ? "du logement" : "global (legacy)"} —{" "}
          <strong>{resume.taux_pct} %</strong>, plafond <strong>{resume.plafond_eur.toFixed(2)} €</strong>/nuit/pers
          {resume.mineurs_exoneres ? ", mineurs exonérés" : ", mineurs NON exonérés"}.
        </FicheAlert>
      ) : (
        <FicheAlert tone="warning" testId="taxe-bareme-manquant">
          <strong>Barème taxe à valider</strong> — sans barème, ce logement ne produit
          aucun devis Direct (les visiteurs voient « bientôt disponible »).
          {parsed
            ? " Détection en cours sur l'adresse saisie…"
            : " Saisissez l'adresse complète au format « rue, CP ville » pour lancer la détection."}
        </FicheAlert>
      )}

      {applied && (
        <FicheAlert tone="success" style={{ marginTop: 6 }}>
          ✓ Barème appliqué et enregistré.
        </FicheAlert>
      )}
      {applyError && (
        <FicheAlert tone="danger" style={{ marginTop: 6 }} testId="taxe-apply-error">
          {applyError}
        </FicheAlert>
      )}

      {/* Résultats de détection. */}
      {status === "loading" && (
        <div style={{ fontSize: 12, color: DS.textMuted, marginTop: 6 }}>Recherche du barème…</div>
      )}
      {status === "error" && (
        <FicheAlert tone="danger" style={{ marginTop: 6 }}>
          Détection indisponible — saisissez le barème manuellement ci-dessous.
        </FicheAlert>
      )}

      {status === "done" && result?.match === "valide" && !applied && (
        <div style={{ marginTop: 8 }} data-testid="taxe-detect-valide">
          <FicheAlert tone="info">
            Barème détecté : <strong>{result.bareme.commune_nom}</strong>
            {result.bareme.epci ? ` (${result.bareme.epci})` : ""} —{" "}
            <strong>{result.bareme.taux_pct} %</strong>, plafond{" "}
            <strong>{Number(result.bareme.plafond_eur).toFixed(2)} €</strong>/nuit/pers
            {result.bareme.mineurs_exoneres ? ", mineurs exonérés" : ""}.
          </FicheAlert>
          <div style={{ marginTop: 6 }}>
            <PrimaryButton
              onClick={() => applyBareme({
                taux_pct: Number(result.bareme.taux_pct),
                plafond_eur: Number(result.bareme.plafond_eur),
                mineurs_exoneres: result.bareme.mineurs_exoneres !== false,
              }, result.bareme.source_url || `catalogue — ${result.bareme.commune_nom}`)}
              disabled={applying}
              style={{ fontSize: 12 }}
            >
              {applying ? "Application…" : "Appliquer ce barème"}
            </PrimaryButton>
          </div>
        </div>
      )}

      {status === "done" && (result?.match === "propose" || result?.match === "externe") && !applied && (
        <div style={{ marginTop: 8 }} data-testid="taxe-detect-proposition">
          <FicheAlert tone="warning">
            Proposition trouvée{" "}
            {result.match === "externe" ? "(source : base DELTA / data.gouv)" : "(catalogue, non validée)"} —{" "}
            {result.match === "externe" ? (
              <>
                taux communal <strong>BRUT {result.proposition.taux_brut_pct} %</strong>
                {result.proposition.tarif_total_pct
                  ? <> · total publié additionnelles incluses : <strong>{result.proposition.tarif_total_pct} %</strong></>
                  : null}
                {result.proposition.plafond_hint_eur
                  ? <> · indice plafond : <strong>{result.proposition.plafond_hint_eur.toFixed(2)} €</strong></>
                  : null}
                {" · "}
                <a href={result.proposition.source_url} target="_blank" rel="noreferrer" style={{ color: DS.ciel, fontWeight: 600 }}>
                  voir la source
                </a>
              </>
            ) : (
              <>
                <strong>{result.bareme.taux_pct ?? "?"} %</strong>, plafond{" "}
                <strong>{result.bareme.plafond_eur ?? "?"} €</strong>
                {result.bareme.source_url ? (
                  <>
                    {" · "}
                    <a href={result.bareme.source_url} target="_blank" rel="noreferrer" style={{ color: DS.ciel, fontWeight: 600 }}>
                      voir la source
                    </a>
                  </>
                ) : null}
              </>
            )}
          </FicheAlert>
          {!manual && (
            <div style={{ marginTop: 6 }}>
              <SecondaryButton
                onClick={() => setManual(result.match === "externe"
                  ? {
                      // Préremplissage : tarif_total (additionnelles incluses) si
                      // publié, sinon taux BRUT (à corriger par Dimitri) ; plafond
                      // = indice DELTA s'il existe.
                      taux_pct: result.proposition.tarif_total_pct ?? result.proposition.taux_brut_pct,
                      plafond_eur: result.proposition.plafond_hint_eur ?? "",
                      mineurs_exoneres: true,
                      source_url: result.proposition.source_url,
                    }
                  : {
                      id: result.bareme.id,
                      taux_pct: result.bareme.taux_pct ?? "",
                      plafond_eur: result.bareme.plafond_eur ?? "",
                      mineurs_exoneres: result.bareme.mineurs_exoneres !== false,
                      source_url: result.bareme.source_url,
                    })}
                style={{ fontSize: 12 }}
              >
                Valider ce barème…
              </SecondaryButton>
            </div>
          )}
          {manual && manualForm("Vérifiez puis validez le barème (écrit le catalogue + applique au logement) :")}
        </div>
      )}

      {status === "done" && result?.match === "none" && !applied && (
        <div style={{ marginTop: 8 }} data-testid="taxe-detect-none">
          <FicheAlert tone="warning">
            Aucun barème fiable trouvé pour <strong>{parsed?.city}</strong> ({parsed?.postal_code}) —
            saisissez-le manuellement depuis le site de la commune/EPCI.
          </FicheAlert>
          {!manual && (
            <div style={{ marginTop: 6 }}>
              <SecondaryButton onClick={() => setManual({ taux_pct: "", plafond_eur: "", mineurs_exoneres: true })} style={{ fontSize: 12 }}>
                Saisir manuellement…
              </SecondaryButton>
            </div>
          )}
          {manual && manualForm("Saisie manuelle du barème :")}
        </div>
      )}

      {/* Relance manuelle (utile après correction d'adresse ou pour re-vérifier). */}
      {parsed && !applying && (
        <div style={{ marginTop: 6 }}>
          <button
            type="button"
            onClick={() => runDetect(parsed.city, parsed.postal_code)}
            style={{ background: "none", border: "none", padding: 0, fontSize: 11, color: DS.ciel, cursor: "pointer", textDecoration: "underline" }}
            data-testid="taxe-detect-retry"
          >
            {baremeOk ? "Re-vérifier le barème de la commune" : "Relancer la détection"}
          </button>
        </div>
      )}
    </div>
  );
}
